import { createFileRoute, useParams, useNavigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { trpc } from '@/lib/trpc'
import { useState, useEffect, useMemo } from 'react'
import { useChat } from '@ai-sdk/react'
import { DefaultChatTransport } from 'ai'
import { apiEndpoints } from '@/lib/env'
import {
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronDown,
  ChevronRight,
  ArrowRight,
  Ban,
  FileText,
  FlaskConical,
  Info,
} from 'lucide-react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ScreenataStepLayout } from '@/components/screenata/ScreenataStepLayout'
import { cn } from '@/lib/utils'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/screenata/test-outline',
)({
  component: TestOutlinePage,
})

type TestItem = {
  statusId: string
  title: string
  description: string | null
  system: string
  type: string
  templateType: string
  category: 'policy' | 'test'
  controlRefs: string[]
  necessity: 'required' | 'common-ask' | 'scope-dependent' | 'optional'
  necessityReason: string | null
  necessitySource?: 'type-i-baseline' | 'auditor-ask' | 'scope-selected' | null
  evidenceEffort: 'low' | 'medium' | 'high' | null
  isNA: boolean
  naReason: string | null
  enhanceStatus: string | null
}

type ControlGroup = {
  controlRef: string
  controlTitle: string
  controlCategory: string
  tests: TestItem[]
}

type EnhancementStreamEvent = {
  seq: number
  timestamp: string
  kind: string
  level: 'info' | 'progress' | 'pass' | 'warn' | 'fail'
  message: string
  controlRef?: string
  counts?: Record<string, number>
  details?: Record<string, unknown>
}

function TestOutlinePage() {
  const { workspaceId } = useParams({
    from: '/(protected)/workspaces/$workspaceId/screenata/test-outline',
  })
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const programQuery = useQuery(
    trpc.complianceProgram.getProgram.queryOptions({ framework: 'SOC2' }),
  )
  const programId = programQuery.data?.id

  const outlineQuery = useQuery({
    ...trpc.complianceProgram.getTestPlanOutline.queryOptions({
      programId: programId ?? '',
    }),
    enabled: !!programId,
  })

  const phase =
    outlineQuery.data?.phase ?? programQuery.data?.testPlanPhase ?? null
  const allControls = useMemo(
    () => ((outlineQuery.data?.controls ?? []) as ControlGroup[]),
    [outlineQuery.data?.controls],
  )

  // View toggle: minimum pack (required) vs full checklist
  const auditType =
    (programQuery.data as { auditType?: string } | undefined)?.auditType ??
    'TYPE_I'
  const [mode, setMode] = useState<'MINIMUM' | 'BALANCED'>(auditType === 'TYPE_II' ? 'BALANCED' : 'MINIMUM')

  // Filter controls based on mode
  const controls = useMemo(() => {
    if (mode === 'BALANCED') return allControls
    return allControls.map((group) => ({
      ...group,
      tests: group.tests.filter(
        (t) => t.isNA || t.necessity === 'required',
      ),
    })).filter((group) => group.tests.length > 0)
  }, [allControls, mode])

  const token = localStorage.getItem('auth:token')
  const activeWorkspaceId = localStorage.getItem('auth:activeWorkspaceId') || workspaceId
  const testEnhanceTransport = useMemo(
    () => new DefaultChatTransport({
      api: apiEndpoints.screenataTestEnhance,
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Workspace-Id': activeWorkspaceId,
      },
      credentials: 'include',
    }),
    [token, activeWorkspaceId],
  )

  const {
    messages: enhanceMessages,
    status: enhanceStreamStatus,
    sendMessage: sendEnhanceMessage,
    setMessages: setEnhanceMessages,
  } = useChat({
    id: `screenata-test-enhance-${programId ?? 'unknown'}`,
    transport: testEnhanceTransport,
  })
  const isStreamingEnhancement =
    enhanceStreamStatus === 'submitted' || enhanceStreamStatus === 'streaming'

  const streamEvents = useMemo(() => {
    const events: EnhancementStreamEvent[] = []
    for (const message of enhanceMessages as Array<{
      parts?: Array<{ type?: string; data?: unknown }>
    }>) {
      const parts = Array.isArray(message?.parts) ? message.parts : []
      for (const part of parts) {
        if (part?.type !== 'data-test-enhancement-event') continue
        const data = part?.data
        if (!data || typeof data !== 'object') continue
        if (typeof data.seq !== 'number' || typeof data.message !== 'string') continue
        events.push(data as EnhancementStreamEvent)
      }
    }
    return events.sort((a, b) => a.seq - b.seq)
  }, [enhanceMessages])

  // Progress polling (only when enhancing)
  const progressQuery = useQuery({
    ...trpc.complianceProgram.getTestEnhancementProgress.queryOptions({
      programId: programId ?? '',
    }),
    enabled: !!programId && (phase === 'enhancing' || isStreamingEnhancement),
    refetchInterval:
      phase === 'enhancing' || isStreamingEnhancement ? 2000 : false,
  })

  // When progress says complete, refetch outline + program
  useEffect(() => {
    if (progressQuery.data?.phase === 'complete') {
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getTestPlanOutline.queryKey(),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getProgram.queryKey(),
      })
    }
  }, [progressQuery.data?.phase, queryClient])

  // View mode
  const [viewMode, setViewMode] = useState<'control' | 'type'>('type')

  // Deduplicated items (policies appear in multiple control groups)
  const uniqueItemMap = useMemo(() => {
    const map = new Map<string, TestItem>()
    for (const group of controls) {
      for (const test of group.tests) {
        if (!map.has(test.statusId)) {
          map.set(test.statusId, test)
        }
      }
    }
    return map
  }, [controls])

  const enhanceableStatusIds = useMemo(() => {
    const ids: string[] = []
    for (const [id, item] of uniqueItemMap) {
      // Only enhance deterministic test-plan items here.
      // Policy/deliverable artifacts are generated in the policy pipeline.
      if (
        !item.isNA &&
        item.category === 'test' &&
        item.enhanceStatus !== 'enhanced'
      ) {
        ids.push(id)
      }
    }
    return ids
  }, [uniqueItemMap])

  const totalAllTests = allControls.reduce((sum, c) => sum + c.tests.length, 0)

  // Auto-collapse N/A-only controls
  const [collapseInitialized, setCollapseInitialized] = useState(false)
  useEffect(() => {
    if (controls.length > 0 && !collapseInitialized) {
      const naOnlyControls = controls
        .filter((c) => c.tests.every((t) => t.isNA))
        .map((c) => c.controlRef)
      setCollapsedControls(new Set(naOnlyControls))
      setCollapseInitialized(true)
    }
  }, [controls, collapseInitialized])

  // Collapsed controls
  const [collapsedControls, setCollapsedControls] = useState<Set<string>>(
    new Set(),
  )

  const toggleControl = (ref: string) => {
    setCollapsedControls((prev) => {
      const next = new Set(prev)
      if (next.has(ref)) next.delete(ref)
      else next.add(ref)
      return next
    })
  }

  const handleStartEnhancement = async () => {
    if (!programId || enhanceableStatusIds.length === 0 || isStreamingEnhancement) {
      return
    }
    setEnhanceMessages([])
    sendEnhanceMessage(
      {
        text: `Enhance ${enhanceableStatusIds.length} selected tests`,
      },
      {
        body: {
          programId,
          selectedStatusIds: enhanceableStatusIds,
        },
      },
    )
    queryClient.invalidateQueries({
      queryKey: trpc.complianceProgram.getTestPlanOutline.queryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.complianceProgram.getProgram.queryKey(),
    })
  }

  const totalTests = uniqueItemMap.size
  const naTests = [...uniqueItemMap.values()].filter((t) => t.isNA).length

  const progressSummary = progressQuery.data?.summary
  const effectivePhase =
    isStreamingEnhancement && phase === 'outline' ? 'enhancing' : phase

  // Derived data for "By Type" view
  const { policyItems, testItems } = useMemo(() => {
    const policies: TestItem[] = []
    const tests: TestItem[] = []
    for (const item of uniqueItemMap.values()) {
      if (item.category === 'policy') policies.push(item)
      else tests.push(item)
    }
    return { policyItems: policies, testItems: tests }
  }, [uniqueItemMap])

  if (programQuery.isLoading || outlineQuery.isLoading) {
    return (
      <ScreenataStepLayout section="Evidence checklist" title="Loading..." alignTop>
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </ScreenataStepLayout>
    )
  }

  const title =
    effectivePhase === 'outline'
      ? 'Review Evidence Checklist'
      : effectivePhase === 'enhancing'
        ? 'Generating Checklist Details...'
        : effectivePhase === 'complete'
          ? 'Evidence Checklist Ready'
          : 'Evidence Checklist'

  const description =
    effectivePhase === 'outline'
      ? `${totalTests} items across ${controls.length} controls${mode === 'MINIMUM' && totalAllTests > totalTests ? ` (${totalAllTests} total in full view)` : ''}. ${naTests} marked N/A.`
      : effectivePhase === 'enhancing'
        ? 'AI is generating detailed checklist instructions. You can leave and come back.'
        : effectivePhase === 'complete'
          ? 'All checklist items have detailed instructions.'
          : undefined

  const topRight =
    effectivePhase === 'outline' ? (
      <div className="flex items-center gap-3 shrink-0">
        <div className="flex items-center rounded-lg border bg-white p-0.5">
          <button
            onClick={() => setMode('MINIMUM')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              mode === 'MINIMUM'
                ? 'bg-emerald-100 text-emerald-800'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Required pack
          </button>
          <button
            onClick={() => setMode('BALANCED')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              mode === 'BALANCED'
                ? 'bg-emerald-100 text-emerald-800'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Show all
          </button>
        </div>
      </div>
    ) : undefined

  const footer = (
    <>
      {effectivePhase === 'outline' && (
        enhanceableStatusIds.length > 0 ? (
          <button
            onClick={handleStartEnhancement}
            disabled={isStreamingEnhancement}
            className="inline-flex items-center gap-2 rounded-lg bg-foreground px-5 py-2.5 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {isStreamingEnhancement ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
        ) : (
          <button
            onClick={() =>
              navigate({
                to: '/workspaces/$workspaceId/screenata/tests',
                params: { workspaceId },
              })
            }
            className="inline-flex items-center gap-2 rounded-lg bg-foreground px-5 py-2.5 text-sm font-medium text-background transition-opacity hover:opacity-90"
          >
            Open checklist
            <ArrowRight className="h-4 w-4" />
          </button>
        )
      )}
      {effectivePhase === 'enhancing' && !isStreamingEnhancement && (
        <button
          onClick={handleStartEnhancement}
          disabled={enhanceableStatusIds.length === 0}
          className="inline-flex items-center gap-2 rounded-lg border border-foreground/20 px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
        >
          Restart enhancement
        </button>
      )}
      {effectivePhase === 'complete' && enhanceableStatusIds.length > 0 && (
        <button
          onClick={handleStartEnhancement}
          className="inline-flex items-center gap-2 rounded-lg border border-foreground/20 px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
        >
          Re-run remaining
        </button>
      )}
      {effectivePhase === 'complete' && (
        <button
          onClick={() =>
            navigate({
              to: '/workspaces/$workspaceId/screenata/tests',
              params: { workspaceId },
            })
          }
          className="inline-flex items-center gap-2 rounded-lg bg-foreground px-5 py-2.5 text-sm font-medium text-background transition-opacity hover:opacity-90"
        >
          Open checklist
          <ArrowRight className="h-4 w-4" />
        </button>
      )}
    </>
  )

  const handleNext =
    effectivePhase === 'outline'
      ? enhanceableStatusIds.length > 0
        ? handleStartEnhancement
        : () =>
            navigate({
              to: '/workspaces/$workspaceId/screenata/tests',
              params: { workspaceId },
            })
      : effectivePhase === 'complete'
        ? () =>
            navigate({
              to: '/workspaces/$workspaceId/screenata/tests',
              params: { workspaceId },
            })
        : undefined

  return (
    <ScreenataStepLayout
      section="Build evidence checklist from auditor matrix"
      title={title}
      description={description}
      topRight={topRight}
      footer={footer}
      alignTop
      onBack={() =>
        navigate({
          to: '/workspaces/$workspaceId/screenata',
          params: { workspaceId },
        })
      }
      onNext={handleNext}
      disableNext={isStreamingEnhancement}
    >
      {/* Progress bar (enhancing or complete) */}
      {(effectivePhase === 'enhancing' || effectivePhase === 'complete') &&
        progressSummary && (
        <div className="mb-6 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {progressSummary.enhanced} of {progressSummary.total} enhanced
              {progressSummary.failed > 0 &&
                ` (${progressSummary.failed} failed)`}
            </span>
            {effectivePhase === 'enhancing' && (
              <span className="flex items-center gap-1.5 text-primary">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Processing...
              </span>
            )}
          </div>
          <div className="w-full rounded-full bg-muted h-2">
            <div
              className="h-2 rounded-full bg-primary transition-all duration-500"
              style={{
                width: `${progressSummary.total > 0 ? ((progressSummary.enhanced + progressSummary.failed) / progressSummary.total) * 100 : 0}%`,
              }}
            />
          </div>
        </div>
      )}

      {(isStreamingEnhancement || streamEvents.length > 0) && (
        <EnhancementStreamPanel
          events={streamEvents}
          isRunning={isStreamingEnhancement}
        />
      )}

      {/* Disclaimer banner */}
      <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50/50 px-4 py-3">
        <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
        <p className="text-xs text-blue-700">
          These labels reflect typical SOC 2 Type I auditor expectations. Your auditor may have different requirements — confirm with your audit firm.
        </p>
      </div>

      {/* View toggle */}
      {controls.length > 0 && (
        <div className="mb-4">
          <Tabs
            value={viewMode}
            onValueChange={(v) => setViewMode(v as 'control' | 'type')}
          >
            <TabsList>
              <TabsTrigger value="control">By Control</TabsTrigger>
              <TabsTrigger value="type">By Type</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}

      {/* By Control view */}
      {viewMode === 'control' && (
        <div className="space-y-2">
          {controls.map((group) => (
            <ControlGroupCard
              key={group.controlRef}
              group={group}
              phase={effectivePhase}
              isCollapsed={collapsedControls.has(group.controlRef)}
              onToggleCollapse={() => toggleControl(group.controlRef)}
            />
          ))}
        </div>
      )}

      {/* By Type view */}
      {viewMode === 'type' && (
        <div className="space-y-6">
          {/* Policies section */}
          <TypeSection
            label="Policies"
            icon={<FileText className="w-4 h-4" />}
            items={policyItems}
            phase={effectivePhase}
            showControlRefs
          />

          {/* Evidence tests section */}
          <TypeSection
            label="Proof Checks"
            icon={<FlaskConical className="w-4 h-4" />}
            items={testItems}
            phase={effectivePhase}
            showSystem
          />
        </div>
      )}
    </ScreenataStepLayout>
  )
}

function EnhancementStreamPanel({
  events,
  isRunning,
}: {
  events: EnhancementStreamEvent[]
  isRunning: boolean
}) {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const rows = events.length > 0
    ? events
    : [{
        seq: 0,
        timestamp: new Date().toISOString(),
        kind: 'run-start',
        level: 'progress' as const,
        message: 'Preparing enhancement run...',
      }]

  const toggleExpanded = (seq: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(seq)) next.delete(seq)
      else next.add(seq)
      return next
    })
  }

  return (
    <div className="mb-6 space-y-3 rounded-xl border bg-muted/10 p-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium">Agent run</p>
        {isRunning && (
          <span className="inline-flex items-center gap-1.5 text-xs text-primary">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Streaming progress...
          </span>
        )}
      </div>
      <div className="max-h-[220px] overflow-y-auto rounded-lg border bg-background/70 p-2.5 text-sm">
        <div className="space-y-1.5">
          {rows.map((event) => {
            const hasDetails = !!event.controlRef || !!event.counts || !!event.details
            const isExpanded = expandedIds.has(event.seq)
            return (
              <div key={`${event.seq}-${event.timestamp}`} className="space-y-1.5">
                <div className="flex items-start gap-2">
                  <span className="mt-0.5 shrink-0">
                    <EnhancementEventIcon level={event.level} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-foreground/80">{event.message}</span>
                      {hasDetails && (
                        <button
                          type="button"
                          onClick={() => toggleExpanded(event.seq)}
                          className="shrink-0 text-xs text-muted-foreground underline-offset-2 hover:underline"
                        >
                          {isExpanded ? 'Hide details' : 'Show details'}
                        </button>
                      )}
                    </div>
                    {hasDetails && isExpanded && (
                      <div className="mt-1.5 space-y-1 rounded-md border bg-muted/30 p-2 text-xs text-foreground/80">
                        {event.controlRef && (
                          <p>
                            <span className="text-muted-foreground">Control:</span>{' '}
                            <span className="font-mono">{event.controlRef}</span>
                          </p>
                        )}
                        {event.counts && (
                          <pre className="overflow-auto whitespace-pre-wrap rounded bg-background/70 p-2 text-[11px]">
                            {JSON.stringify(event.counts, null, 2)}
                          </pre>
                        )}
                        {event.details && (
                          <pre className="overflow-auto whitespace-pre-wrap rounded bg-background/70 p-2 text-[11px]">
                            {JSON.stringify(event.details, null, 2)}
                          </pre>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function EnhancementEventIcon({
  level,
}: {
  level: EnhancementStreamEvent['level']
}) {
  if (level === 'pass') return <span className="text-emerald-500">✓</span>
  if (level === 'warn') return <span className="text-amber-500">⚠</span>
  if (level === 'fail') return <span className="text-red-500">✗</span>
  if (level === 'progress') {
    return <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
  }
  return <span className="text-blue-500">→</span>
}

// ── Control Group Card (By Control view) ──────────────────────────────────

function ControlGroupCard({
  group,
  phase,
  isCollapsed,
  onToggleCollapse,
}: {
  group: ControlGroup
  phase: string | null
  isCollapsed: boolean
  onToggleCollapse: () => void
}) {
  const applicableInGroup = group.tests.filter((t) => !t.isNA)
  const enhancedInGroup = group.tests.filter(
    (t) => t.enhanceStatus === 'enhanced',
  )
  const isAllNA = applicableInGroup.length === 0

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border',
        isAllNA && 'opacity-50',
      )}
    >
      <button
        onClick={onToggleCollapse}
        className="w-full flex items-center gap-3 px-4 py-3 bg-muted/40 hover:bg-muted/70 transition-colors text-left"
      >
        {isCollapsed ? (
          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
        )}
        <span
          className={cn(
            'font-mono text-sm font-medium',
            isAllNA ? 'text-muted-foreground' : 'text-primary',
          )}
        >
          {group.controlRef}
        </span>
        <span className="text-sm text-muted-foreground truncate">
          {group.controlTitle}
        </span>
        {isAllNA && (
          <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded shrink-0">
            N/A
          </span>
        )}
        <div className="ml-auto flex items-center gap-2 shrink-0">
          {phase === 'outline' && !isAllNA && (
            <span className="text-xs text-muted-foreground">
              {applicableInGroup.length} item{applicableInGroup.length !== 1 ? 's' : ''}
            </span>
          )}
          {(phase === 'enhancing' || phase === 'complete') && !isAllNA && (
            <span className="text-xs text-muted-foreground">
              {enhancedInGroup.length}/{applicableInGroup.length}
            </span>
          )}
        </div>
      </button>

      {!isCollapsed && (
        <div className="divide-y divide-border">
          {group.tests.map((test) => (
            <TestRow
              key={test.statusId}
              test={test}
              phase={phase}
              showCategoryBadge
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ── Type Section (By Type view) ───────────────────────────────────────────

function TypeSection({
  label,
  icon,
  items,
  phase,
  showControlRefs,
  showSystem,
}: {
  label: string
  icon: React.ReactNode
  items: TestItem[]
  phase: string | null
  showControlRefs?: boolean
  showSystem?: boolean
}) {
  const applicable = items.filter((t) => !t.isNA)
  const naItems = items.filter((t) => t.isNA)

  if (items.length === 0) return null

  return (
    <div>
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
        {icon}
        {label}
        <span className="font-normal">({applicable.length})</span>
      </div>
      <div className="overflow-hidden rounded-xl border divide-y divide-border">
        {applicable.map((test) => (
          <TestRow
            key={test.statusId}
            test={test}
            phase={phase}
            showControlRefs={showControlRefs}
            showSystem={showSystem}
          />
        ))}
        {naItems.length > 0 && (
          <div className="px-4 py-2 text-xs text-muted-foreground bg-muted/20">
            {naItems.length} N/A item{naItems.length !== 1 ? 's' : ''} hidden
          </div>
        )}
      </div>
    </div>
  )
}

// ── Test Row ──────────────────────────────────────────────────────────────

function TestRow({
  test,
  phase,
  showCategoryBadge,
  showControlRefs,
  showSystem,
}: {
  test: TestItem
  phase: string | null
  showCategoryBadge?: boolean
  showControlRefs?: boolean
  showSystem?: boolean
}) {
  const isNA = test.isNA
  const hasRealSystem =
    test.system && test.system !== 'manual-process' && test.system !== 'policy'

  return (
    <div
      className={cn(
        'flex items-start gap-3 px-4 py-3',
        isNA && 'bg-muted/20 text-muted-foreground',
      )}
    >
      <div className="pt-0.5">
        {phase === 'outline' ? (
          isNA ? (
            <Ban className="w-4 h-4 text-muted-foreground/50 shrink-0" />
          ) : (
            <CheckCircle2 className="w-4 h-4 text-emerald-500/60 shrink-0" />
          )
        ) : (
          <EnhanceStatusIcon status={test.enhanceStatus} isNA={isNA} />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={cn('text-sm font-medium', isNA && 'line-through')}
          >
            {test.title}
          </span>
          {showCategoryBadge && (
            <CategoryBadge category={test.category} />
          )}
          {test.category === 'test' && (
            <TemplateTypeBadge templateType={test.templateType} />
          )}
          {!isNA && (
            <>
              {(() => {
                const source = test.necessitySource
                  ?? (test.necessity === 'scope-dependent'
                    ? 'scope-selected'
                    : test.necessity === 'common-ask'
                      ? 'auditor-ask'
                      : null)
                const primaryLabel = test.necessity === 'required'
                  ? source === 'auditor-ask'
                    ? 'Required (Auditor ask)'
                    : 'Required for Type I pack'
                  : 'Optional proof checks'
                return (
                  <span
                    className={
                      test.necessity === 'required'
                        ? 'text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded shrink-0'
                        : 'text-xs text-slate-700 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded shrink-0'
                    }
                    title={test.necessityReason ?? undefined}
                  >
                    {primaryLabel}
                  </span>
                )
              })()}
              {(() => {
                if (test.necessity === 'required') return null
                const source = test.necessitySource
                  ?? (test.necessity === 'scope-dependent'
                    ? 'scope-selected'
                    : test.necessity === 'common-ask'
                      ? 'auditor-ask'
                      : null)
                const sourceLabel = source === 'type-i-baseline'
                  ? 'Type I baseline'
                  : source === 'scope-selected'
                    ? 'Scope-selected'
                    : source === 'auditor-ask'
                      ? 'Auditor ask'
                      : null
                if (!sourceLabel) return null
                return (
                  <span
                    className="text-xs text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded shrink-0"
                    title={test.necessityReason ?? undefined}
                  >
                    {sourceLabel}
                  </span>
                )
              })()}
            </>
          )}
          {test.evidenceEffort && !isNA && (
            <span
              className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${
                test.evidenceEffort === 'low'
                  ? 'text-emerald-600 bg-emerald-50'
                  : test.evidenceEffort === 'high'
                    ? 'text-red-600 bg-red-50'
                    : 'text-amber-600 bg-amber-50'
              }`}
            >
              {test.evidenceEffort === 'low' ? 'Low' : test.evidenceEffort === 'high' ? 'High' : 'Med'} effort
            </span>
          )}
          {showSystem && hasRealSystem && (
            <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0">
              {formatSystemName(test.system)}
            </span>
          )}
        </div>
        {test.description && !isNA && (
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
            {test.description}
          </p>
        )}
        {showControlRefs && test.controlRefs.length > 0 && (
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {test.controlRefs.map((ref) => (
              <span
                key={ref}
                className="text-xs font-mono text-primary/70 bg-primary/5 px-1.5 py-0.5 rounded"
              >
                {ref}
              </span>
            ))}
          </div>
        )}
      </div>

      {isNA && test.naReason && (
        <span
          className="text-xs text-muted-foreground truncate max-w-[200px] shrink-0"
          title={test.naReason}
        >
          {test.naReason}
        </span>
      )}
    </div>
  )
}

// ── Category Badge ────────────────────────────────────────────────────────

function CategoryBadge({ category }: { category: 'policy' | 'test' }) {
  return (
    <span
      className={cn(
        'shrink-0 rounded px-1.5 py-0.5 text-xs font-medium',
        category === 'policy'
          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
          : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      )}
    >
      {category === 'policy' ? 'Policy' : 'Evidence'}
    </span>
  )
}

// ── Template Type Badge ───────────────────────────────────────────────────

const TEMPLATE_TYPE_LABELS: Record<string, string> = {
  GUIDED: 'Manual',
  WORKFLOW: 'Automated',
  STATIC_SCREENSHOT: 'Screenshot',
}

function TemplateTypeBadge({ templateType }: { templateType: string }) {
  const label = TEMPLATE_TYPE_LABELS[templateType]
  if (!label) return null
  return (
    <span className="shrink-0 rounded px-1.5 py-0.5 text-xs text-muted-foreground bg-muted/80">
      {label}
    </span>
  )
}

// ── System Name Formatter ────────────────────────────────────────────────

function formatSystemName(system: string): string {
  const KNOWN: Record<string, string> = {
    aws: 'AWS',
    gcp: 'GCP',
    github: 'GitHub',
    gitlab: 'GitLab',
    'google-workspace': 'Google Workspace',
    jira: 'Jira',
    slack: 'Slack',
  }
  return (
    KNOWN[system] ??
    system
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  )
}

// ── Enhance Status Icon ───────────────────────────────────────────────────

function EnhanceStatusIcon({
  status,
  isNA,
}: {
  status: string | null
  isNA: boolean
}) {
  if (isNA) {
    return <Ban className="w-4 h-4 text-muted-foreground/50 shrink-0" />
  }
  if (status === 'enhanced') {
    return <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
  }
  if (status === 'enhancing') {
    return (
      <Loader2 className="w-4 h-4 text-blue-500 animate-spin shrink-0" />
    )
  }
  if (status === 'failed') {
    return <XCircle className="w-4 h-4 text-red-500 shrink-0" />
  }
  if (status === 'pending') {
    return <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
  }
  return <Clock className="w-4 h-4 text-muted-foreground/50 shrink-0" />
}
